import { User, Bot, Volume2, Copy, Check, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import type { Message } from '../../types/message';
import { formatTime, copyToClipboard } from '../../utils/formatters';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm'

interface MessageBubbleProps {
  message: Message;
  onPlayAudio?: () => void;
  onRetry?: () => void;
}

export default function MessageBubble({ message, onPlayAudio, onRetry }: MessageBubbleProps) {
  const [copied, setCopied] = useState(false);
  const isUser = message.type === 'user';
  const isSystem = message.type === 'system';
  const isError = isSystem && (message.content.toLowerCase().includes('error') || message.content.includes('Lo siento, hubo un error'));

  const handleCopy = async () => {
    const success = await copyToClipboard(message.content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <span className="text-sm text-base-content/50 italic">
          {message.content}
          {isError && onRetry && (
            <button
              onClick={onRetry}
              className="btn btn-ghost btn-xs btn-circle ml-2"
              aria-label="Reintentar"
              title="Reintentar"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${isUser ? 'bg-primary' : 'bg-secondary'
        }`}>
        {isUser ? (
          <User className="w-5 h-5 text-primary-content" />
        ) : (
          <Bot className="w-5 h-5 text-secondary-content flex-1" />
        )}
      </div>

      {/* Message Content */}
      <div className={`flex flex-col max-w-[75%] ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`relative group ${isUser ? 'message-bubble-user' : 'message-bubble-avatar'}`}>
          <Markdown remarkPlugins={[remarkGfm]}>
            {message.content}
          </Markdown>
          {/* Voice indicator */}
          {message.isVoice && (
            <span className="absolute -bottom-1 -right-1 text-xs">
              🎤
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-1 group-hover:opacity-100 transition-opacity">
          <span className="text-xs text-base-content/40">
            {formatTime(message.timestamp)}
          </span>

          {!isUser && onPlayAudio && (
            <button
              onClick={onPlayAudio}
              className="btn btn-ghost btn-xs btn-circle"
              aria-label="Reproducir audio"
              title="Reproducir audio"
            >
              <Volume2 className="w-3 h-3" />
            </button>
          )}

          <button
            onClick={handleCopy}
            className="btn btn-ghost btn-xs btn-circle"
            aria-label="Copiar mensaje"
            title="Copiar mensaje"
          >
            {copied ? (
              <Check className="w-3 h-3 text-success" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}